import React, { useMemo } from 'react';
import { MultiLineChart } from '../ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportChartDataToCSV } from '../../utils/exportToCSV';

/**
 * Multi-Line Widget Component
 * Modernized replacement for MultiLineList.js
 * Displays multiple line datasets for comparison
 */
function MultiLineWidget({ data }) {
    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Transform API data to Chart.js format
    const chartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const columnData = apiData[0].columnDataSet1;
            if (!columnData || columnData.length === 0) return null;

            // Get labels from first dataset
            const labels = columnData[0].labels;

            // Build datasets array
            const datasets = columnData.map((dataset, index) => {
                const colors = ['#f19f57', '#313131', '#3b82f6', '#10b981', '#f59e0b'];
                const color = colors[index % colors.length];

                return {
                    label: dataset.label || `Dataset ${index + 1}`,
                    data: dataset.data,
                    borderColor: color,
                    backgroundColor: `${color}33`,
                    tension: 0.4,
                    fill: false
                };
            });

            return {
                labels,
                datasets
            };
        } catch (err) {
            console.error('Error transforming multi-line chart data:', err);
            return null;
        }
    }, [apiData, data]);

    // Transform to table format
    const tableData = useMemo(() => {
        if (!chartData) return [];

        return chartData.labels.map((label, index) => {
            const row = { date: label };
            chartData.datasets.forEach((dataset, dsIndex) => {
                row[`value${dsIndex}`] = dataset.data[index];
            });
            return row;
        });
    }, [chartData]);

    // Table columns
    const columns = useMemo(() => {
        if (!chartData) return [];

        const cols = [
            {
                key: 'date',
                label: data.XlabelString || 'Date',
                sortable: true
            }
        ];

        chartData.datasets.forEach((dataset, index) => {
            cols.push({
                key: `value${index}`,
                label: dataset.label,
                sortable: true,
                format: (val) => typeof val === 'number' ? val.toLocaleString() : val
            });
        });

        return cols;
    }, [chartData, data]);

    // Export handler
    const handleExport = () => {
        if (chartData) {
            exportChartDataToCSV(chartData, data.title || 'multi-line-chart-data');
        }
    };

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={
                chartData ? (
                    <MultiLineChart
                        datasets={chartData.datasets}
                        labels={chartData.labels}
                        height={350}
                        options={{
                            scales: {
                                x: {
                                    title: {
                                        display: true,
                                        text: data.XlabelString || ''
                                    }
                                },
                                y: {
                                    title: {
                                        display: true,
                                        text: data.YlabelString || ''
                                    },
                                    beginAtZero: true
                                }
                            }
                        }}
                    />
                ) : null
            }
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        pagination={{ enabled: true, itemsPerPage: 10 }}
                        sortable={true}
                        searchable={true}
                    />
                ) : null
            }
            onExport={handleExport}
            showToggle={true}
            showExport={true}
        />
    );
}

export default MultiLineWidget;
